import styled from 'styled-components';

import { Text } from '../../../../components';
import { BADGE_COLORS } from '@/nova/components/Tab/Tab.styles';
import { ANIM, COLORS, OWN_LIST, SPACE } from '@/nova/theme/tokens';

// Half-transparent background, as the shared Chip gives its count (the number stays opaque).
export const COUNT_BG = `color-mix(in srgb, ${BADGE_COLORS.subtle.bg} 50%, transparent)`;

/* A row of the column, as the page tree draws a page: tinted and in the brand orange while its list is open. It holds
   the grip (where the list can be dragged), the button that opens the list, and the eye that hides or shows it. No
   state changes the weight (the user, 2026-10-17), so nothing shifts when one changes. */
export const Root = styled.div<{ $selected: boolean; $hidden: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 40px;
  border-radius: 8px;
  background: ${(p) => (p.$selected ? COLORS.surfaceMuted : 'transparent')};
  color: ${(p) => (p.$selected ? COLORS.ctaSolid : p.$hidden ? COLORS.textFaint : COLORS.text)};
  transition:
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${(p) => (p.$selected ? COLORS.surfaceMuted : COLORS.hoverWash)};
  }
`;

/* The grip: the row's handle, as the games' rows have (a mouse drags the row from anywhere on it, a resting finger too).
   A button, bare, so a phone's touch near it lands on it. */
export const Grip = styled.button`
  flex: none;
  display: inline-flex;
  align-self: stretch;
  align-items: center;
  margin: 0;
  padding: 0 0 0 ${SPACE[4]};
  border: 0;
  background: none;
  font: inherit;
  color: ${COLORS.textFaint};
  cursor: grab;
  touch-action: none;
`;

/* What opens the list: its name and what tells it apart, then its count. */
export const Main = styled.button`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  align-self: stretch;
  padding: ${SPACE[0]} ${SPACE[8]} ${SPACE[0]} ${SPACE[12]};
  border: none;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  /* Inside the button: a scrolling column would clip a ring drawn outside it. */
  &:focus-visible {
    outline-offset: -2px;
  }
`;

/* The eye: hides the list on the page, or shows it again (crossed out while hidden). Held where the list cannot be. */
export const Visibility = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: ${SPACE[4]};
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    color: ${COLORS.text};
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  &:focus-visible {
    outline-offset: -2px;
  }
`;

/* The list's name, cut on one line when the column is too narrow for it (the row's title holds all of it). */
export const Label = styled(Text)`
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* The unpublished-changes dot, in the publish blue that Publish carries. */
export const Dot = styled.span`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${COLORS.publishBlue};
`;

/* Text for screen readers only (what the muted name and the crossed-out eye say to sighted owners). */
export const ScreenReaderText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  /* The visually-hidden idiom (a 1px box pulled back by its own size), not spacing. */
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

/* Which place of its list a row is, when the page shows the list more than once: muted after the name, never cut. */
export const Appearance = styled.span`
  flex: none;
  color: ${COLORS.textMuted};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`;
